import {DOCUMENT, inject, Injectable} from '@angular/core';
import {AppStorageIdEnum} from '../../enums/app-storage-id.enum';
import {AppStorage} from '../../models/app-storage.model';
import {SentryService} from '../sentry/sentry.service';

@Injectable({
    providedIn: 'root',
})
export class AppStorageService {
    private readonly sentryService = inject(SentryService);
    private readonly window = inject(DOCUMENT).defaultView || window;

    public getStorage(id: AppStorageIdEnum): AppStorage {
        const storage = this.window.localStorage.getItem(id);

        if (!storage) {
            try {
                this.window.localStorage.setItem(id, '{}');
            } catch (error) {
                this.sentryService.captureException(error);
            }
        }

        return this.create(id, this.window);
    }

    private create(id: string, window: Window & typeof globalThis): AppStorage {
        const sentryService = this.sentryService;
        return {
            get<T>(key: string): T | undefined {
                let storage;

                try {
                    storage = JSON.parse(window.localStorage.getItem(id) ?? '{}');
                } catch (error) {
                    sentryService.captureException(error);

                    return undefined;
                }

                if (!storage.hasOwnProperty(key)) {
                    return undefined;
                }

                return storage[key];
            },
            set(key: string, value: unknown): boolean {
                try {
                    const storage = JSON.parse(window.localStorage.getItem(id) ?? '{}');

                    storage[key] = value;

                    window.localStorage.setItem(id, JSON.stringify(storage));
                } catch (error) {
                    sentryService.captureException(error);
                    return false;
                }

                return true;
            },
            has(key: string): boolean {
                return this.keys()
                    ?.includes(key) || false;
            },
            delete(key: string): boolean {
                try {
                    const storage = JSON.parse(window.localStorage.getItem(id) ?? '{}');

                    if (!storage.hasOwnProperty(key)) {
                        return false;
                    }

                    delete storage[key];
                    window.localStorage.setItem(id, JSON.stringify(storage));
                } catch (error) {
                    sentryService.captureException(error);
                    return false;
                }

                return true;
            },
            keys(): Array<string> | null {
                try {
                    const storage = JSON.parse(window.localStorage.getItem(id) ?? '{}');

                    return Object.keys(storage);

                } catch (error) {
                    sentryService.captureException(error);
                }
                return null;
            }
        };
    }
}
